import React, { useCallback, useEffect, useState } from 'react';
import { recommendationsApi, DeviceOptionDetail } from '../../services/api';
import { Button } from '../../ds/components/Button';
import { ModalShell } from '../../ds/ModalShell';
import { ConfirmModal } from '../../ds/ConfirmModal';
import { InputField } from '../../ds/InputField';
import { DEVICE_SECTION_TITLE } from './deviceOptionDefaults';

/**
 * Edytor list pozycji do odznaczenia w zaleceniach dla LOKALI (migracja 054; od 056 także
 * przewody wentylacyjne WK / WŁ / Wwc przy „Kratka wymaga wymiany", od 059 listy nowych pozycji
 * — „ŁĄCZNIK", „Urządzenia wymagają oceny", „Brak Went." i pozycji dodanych w panelu).
 *
 * Bronek, 2026-10-02: „brakuje typów urządzeń (pole do odznaczania) — czy będzie możliwość
 * dodawania pól do odznaczenia w panelu Administratora?". Każda pozycja katalogu z wyborem
 * urządzeń (`devicesMode` ≠ „bez wyboru") ma własną listę: dodawanie, zmiana nazwy / skrótu,
 * kolejność, wycofanie i przywrócenie. Zmiany obowiązują na tablecie po ponownym otwarciu okna
 * zaleceń albo po „Pobierz paczkę" (offline tablet używa ostatnio pobranej listy).
 *
 * Usuwania NIE MA — wycofana pozycja znika z list wyboru, a zalecenia zapisane wcześniej to
 * wolny tekst i zostają bez zmian. Serwer nie wycofa ostatniej aktywnej pozycji listy.
 */

interface Props {
  /** Pozycje katalogu zaleceń lokali z listą urządzeń, w kolejności katalogu (ApartmentRecommendationItemsEditor). */
  recommendations: { id: string; label: string }[];
}

type Sets = Record<string, DeviceOptionDetail[]>;

interface FormState {
  recommendationId: string;
  /** null = nowa pozycja */
  optionId: string | null;
  label: string;
  protocolText: string;
}

/** Tytuł listy: nazwa zalecenia + rodzaj pozycji, gdy to nie urządzenia (kratka: przewody). */
const titleFor = (rec: { id: string; label: string }): string => {
  const kind = DEVICE_SECTION_TITLE[rec.id];
  return kind ? `${rec.label} — ${kind.replace(/:$/, '').toLowerCase()}` : rec.label;
};

const actionStyle: React.CSSProperties = {
  minHeight: 'var(--touch-min)',
  padding: '0 var(--space-3)',
  flexShrink: 0,
};

export const DeviceOptionsEditor: React.FC<Props> = ({ recommendations }) => {
  const [sets, setSets] = useState<Sets>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<
    { recommendationId: string; option: DeviceOptionDetail } | null
  >(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await recommendationsApi.getDeviceOptions();
      setSets(data.sets ?? {});
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Nie udało się pobrać list urządzeń');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const replaceOption = (recommendationId: string, updated: DeviceOptionDetail) => {
    setSets((prev) => {
      const list = prev[recommendationId] ?? [];
      const exists = list.some((o) => o.id === updated.id);
      return {
        ...prev,
        [recommendationId]: exists ? list.map((o) => (o.id === updated.id ? updated : o)) : [...list, updated],
      };
    });
  };

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Nie udało się zapisać zmiany');
    } finally {
      setBusy(false);
    }
  };

  const move = (recommendationId: string, index: number, delta: -1 | 1) =>
    run(async () => {
      const ids = (sets[recommendationId] ?? []).map((o) => o.id);
      const target = index + delta;
      if (target < 0 || target >= ids.length) return;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      const res = await recommendationsApi.reorderDeviceOptions(recommendationId, ids);
      setSets((prev) => ({ ...prev, [recommendationId]: res.options }));
    });

  const setActive = (recommendationId: string, option: DeviceOptionDetail, active: boolean) =>
    run(async () => {
      const updated = await recommendationsApi.updateDeviceOption(recommendationId, option.id, { active });
      replaceOption(recommendationId, updated);
      setNotice(active ? `Przywrócono: ${updated.label}` : `Wycofano z listy: ${updated.label}`);
    });

  const openCreate = (recommendationId: string) => {
    setFormError(null);
    setForm({ recommendationId, optionId: null, label: '', protocolText: '' });
  };

  const openEdit = (recommendationId: string, option: DeviceOptionDetail) => {
    setFormError(null);
    setForm({ recommendationId, optionId: option.id, label: option.label, protocolText: option.protocolText ?? '' });
  };

  const submitForm = async () => {
    if (!form || busy) return;
    const label = form.label.trim();
    if (!label) {
      setFormError('Podaj nazwę urządzenia');
      return;
    }
    const protocolText = form.protocolText.trim() || null;
    setBusy(true);
    setFormError(null);
    try {
      const saved = form.optionId
        ? await recommendationsApi.updateDeviceOption(form.recommendationId, form.optionId, { label, protocolText })
        : await recommendationsApi.createDeviceOption(form.recommendationId, { label, protocolText });
      replaceOption(form.recommendationId, saved);
      setNotice(form.optionId ? `Zapisano: ${saved.label}` : `Dodano: ${saved.label}`);
      setForm(null);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Nie udało się zapisać pozycji');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="device-options-heading" style={{ marginTop: 'var(--space-6)' }}>
      <h2
        id="device-options-heading"
        style={{ fontSize: 'var(--text-h2, 22px)', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 'var(--space-2)' }}
      >
        Typy urządzeń w zaleceniach dla lokali
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4)', maxWidth: 760 }}>
        Pola do odznaczenia „Typ urządzenia" w oknie zaleceń lokalu na tablecie (przy „Kratka
        wymaga wymiany" — przewody wentylacyjne WK, WŁ, Wwc; przy „Brak Went." — to, co stoi po
        „w pomieszczeniu:"). Możesz dodawać
        urządzenia, zmieniać nazwy i kolejność albo wycofać pozycję z listy. „Skrót w treści" to
        to, co trafi do treści zalecenia (np. „PG") — puste pole oznacza pełną nazwę. Zalecenia
        zapisane wcześniej zostają bez zmian.
      </p>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Ładowanie list urządzeń…</p>}
      {loadError && (
        <p role="alert" style={{ color: 'var(--status-deny)' }}>
          {loadError} —{' '}
          <button
            type="button"
            onClick={load}
            style={{ textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', color: 'inherit' }}
          >
            spróbuj ponownie
          </button>
        </p>
      )}
      {actionError && (
        <p role="alert" style={{ color: 'var(--status-deny)', marginBottom: 'var(--space-3)' }}>
          {actionError}
        </p>
      )}
      {notice && (
        <p role="status" style={{ color: 'var(--status-ok)', marginBottom: 'var(--space-3)' }}>
          {notice}
        </p>
      )}

      {!loading && !loadError && recommendations.map((rec) => {
        const recId = rec.id;
        const title = titleFor(rec);
        const list = sets[recId] ?? [];
        return (
          <section
            key={recId}
            aria-label={`Typy urządzeń: ${title}`}
            style={{
              border: 'var(--border-width) solid var(--border)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-5)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-3) var(--space-3) var(--space-4)',
                background: 'var(--surface-fill)',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>{title}</span>
              <Button
                variant="ghost"
                icon="plus"
                onClick={() => openCreate(recId)}
                disabled={busy}
                aria-label={`Dodaj urządzenie: ${title}`}
                style={actionStyle}
              >
                Dodaj urządzenie
              </Button>
            </div>
            {list.length === 0 && (
              <p style={{ padding: 'var(--space-3) var(--space-4)', color: 'var(--text-muted)' }}>
                Lista pusta — dodaj pozycje (tablet pokaże wtedy listę wbudowaną, jeśli pozycja ją ma).
              </p>
            )}
            {list.map((opt, index) => (
              <div
                key={opt.id}
                data-testid={`device-option-${recId}-${opt.id}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  borderTop: 'var(--border-width) solid var(--border)',
                  padding: 'var(--space-2) var(--space-3) var(--space-2) var(--space-4)',
                  opacity: opt.active ? 1 : 0.6,
                }}
              >
                <span style={{ flex: 1, minWidth: 0, color: 'var(--text-primary)' }}>
                  {opt.label}
                  {opt.protocolText && (
                    <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>
                      {' '}— skrót w treści: {opt.protocolText}
                    </span>
                  )}
                  {!opt.active && (
                    <span style={{ color: 'var(--status-deny)', fontSize: 'var(--text-label)', fontWeight: 600 }}>
                      {' '}(wycofane)
                    </span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  icon="chevron-up"
                  onClick={() => move(recId, index, -1)}
                  disabled={busy || index === 0}
                  aria-label={`Przesuń wyżej: ${opt.label}`}
                  style={actionStyle}
                >
                  Wyżej
                </Button>
                <Button
                  variant="ghost"
                  icon="chevron-down"
                  onClick={() => move(recId, index, 1)}
                  disabled={busy || index === list.length - 1}
                  aria-label={`Przesuń niżej: ${opt.label}`}
                  style={actionStyle}
                >
                  Niżej
                </Button>
                <Button
                  variant="ghost"
                  icon="pencil"
                  onClick={() => openEdit(recId, opt)}
                  disabled={busy}
                  aria-label={`Zmień nazwę: ${opt.label}`}
                  style={actionStyle}
                >
                  Zmień
                </Button>
                {opt.active ? (
                  <Button
                    variant="ghost"
                    icon="ban"
                    onClick={() => setConfirmDeactivate({ recommendationId: recId, option: opt })}
                    disabled={busy}
                    aria-label={`Wycofaj: ${opt.label}`}
                    style={actionStyle}
                  >
                    Wycofaj
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    icon="refresh"
                    onClick={() => setActive(recId, opt, true)}
                    disabled={busy}
                    aria-label={`Przywróć: ${opt.label}`}
                    style={actionStyle}
                  >
                    Przywróć
                  </Button>
                )}
              </div>
            ))}
          </section>
        );
      })}

      <ModalShell
        open={form !== null}
        title={form?.optionId ? 'Zmień urządzenie' : `Nowe urządzenie — ${form ? titleFor(recommendations.find((r) => r.id === form.recommendationId) ?? { id: form.recommendationId, label: form.recommendationId }) : ''}`}
        onClose={() => { if (!busy) setForm(null); }}
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={() => setForm(null)} disabled={busy}>Anuluj</Button>
            <Button variant="primary" icon="check" onClick={submitForm} disabled={busy}>
              {busy ? 'Zapisywanie…' : form?.optionId ? 'Zapisz' : 'Dodaj'}
            </Button>
          </>
        }
      >
        {form && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <InputField
              label="Nazwa na liście do odznaczenia"
              value={form.label}
              maxLength={120}
              autoFocus
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              errorMessage={formError ?? undefined}
            />
            <InputField
              label="Skrót w treści zalecenia (opcjonalnie)"
              hint="Np. „PG”. Puste = do treści trafi pełna nazwa."
              value={form.protocolText}
              maxLength={120}
              onChange={(e) => setForm({ ...form, protocolText: e.target.value })}
            />
          </div>
        )}
      </ModalShell>

      <ConfirmModal
        open={confirmDeactivate !== null}
        title="Wycofać urządzenie z listy?"
        message={
          confirmDeactivate
            ? `„${confirmDeactivate.option.label}" zniknie z listy wyboru na tablecie. Zalecenia zapisane wcześniej zostaną bez zmian; pozycję można później przywrócić.`
            : ''
        }
        confirmLabel="Wycofaj"
        destructive
        onCancel={() => setConfirmDeactivate(null)}
        onConfirm={() => {
          const c = confirmDeactivate;
          setConfirmDeactivate(null);
          if (c) setActive(c.recommendationId, c.option, false);
        }}
      />
    </section>
  );
};
